 "use client";
 
import React from "react";
import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/button/button/button";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import styles from "./SectionButtonsDialog.module.css";
 
 interface SectionButtonsDialogProps {
   // Buttons coming from the CMS; we keep this intentionally loose
   buttons?: Array<{
     id?: string | number;
     copy?: string;
     title?: string;
     url?: string;
     type?: string;
     target?: string;
     button_customID?: string;
     size?: string;
   }>;
   align?: "left" | "center" | "right"; // Content alignment from parent section
   /** When true (e.g. in hero), ensure primary buttons appear first */
   prioritizePrimary?: boolean;
 }
 
const SectionButtonsDialog: React.FC<SectionButtonsDialogProps> = ({ buttons, align = "left", prioritizePrimary = false }) => {
  // Normalize buttons if they come in CMS nested format
  const visibleButtons = Array.isArray(buttons)
    ? buttons.map((btn: any) => {
        // If button has .data or .attributes, it's in nested response format
        if (btn.data) return btn.data.attributes || btn.data;
        if (btn.attributes) return btn.attributes;
        return btn;
      })
    : [];

  if (visibleButtons.length === 0) {
     return null;
   }
 

  const mapVariant = (raw: unknown): "primary" | "default" | "ghost" => {
    const val = String(raw || "").toLowerCase();
    if (val === "secondary" || val === "outline") return "default";
    if (val === "ghost" || val === "tertiary") return "ghost";
    return "primary";
  };

   const mapSize = (raw: unknown): "default" | "sm" | "lg" => {
     const val = String(raw || "").toLowerCase();
     if (val === "small" || val === "sm") return "sm";
     if (val === "large" || val === "lg") return "lg";
     return "default";
   };

   // Optionally reorder buttons so primary variants come first (hero sections)
   const orderedButtons = prioritizePrimary
     ? (() => {
         const primary: any[] = [];
         const others: any[] = [];
         visibleButtons.forEach((btn: any) => {
           if (mapVariant(btn.type) === "primary") {
             primary.push(btn);
           } else {
             others.push(btn);
           }
         });
         return [...primary, ...others];
       })()
     : visibleButtons;
 
   // In hero (prioritizePrimary), make buttons full-width with a max width on mobile
   const buttonClassName = cn(styles.button, prioritizePrimary && styles.buttonHero);
 
   return (
    <>
      <div className={styles.row} data-align={align}>
      {orderedButtons.map((btn: any, index: number) => {
         const key = btn.id ?? index;
         const label = btn.copy || btn.title || "Learn more";
         const rawHref = (btn.url || "").trim();
         const targetLower = String(btn.target || "self").toLowerCase();
         const isBlank = targetLower === "blank";
         
          // Check custom IDs for special buttons
          const customId = btn.button_customID?.toLowerCase().trim();
          const isDownloadButton = customId === "download";
          const isEmailLinkButton = customId === "email";
          const isArrowButton = customId === "arrow";
          const showAppleLogo = isDownloadButton;

          const emailHref =
            isEmailLinkButton && rawHref
              ? rawHref.toLowerCase().startsWith("mailto:")
                ? rawHref
                : `mailto:${rawHref}`
              : "";

         const href = rawHref || "#";

         const content = (
           <>
              {showAppleLogo && (
                <Image
                  src="/misc/apple-logo.svg"
                  alt=""
                  width={14}
                  height={17}
                  className={styles.appleLogo}
                  aria-hidden="true"
                />
              )}
              {isArrowButton ? (
                <>
                  <span className={styles.arrowLabel}>
                    {label}
                  </span>
                  <ArrowRight className={styles.arrowIcon} />
                </>
              ) : (
                <span>{label}</span>
              )}
           </>
         );
 
        const isExternal = /^https?:\/\//i.test(href);

          // Simple "email" button: open user's email client via mailto:
          if (isEmailLinkButton && emailHref) {
            return (
              <Button
                key={key}
                asChild
                variant={mapVariant(btn.type)}
                size={mapSize(btn.size)}
                className={buttonClassName}
              >
                <a href={emailHref}>
                  {content}
                </a>
              </Button>
            );
          }
 
        // For both internal and external links, respect target="blank"
        // so CMS authors can choose new-tab behavior explicitly.
        if (href.startsWith("#") || !isExternal) {
          // Internal or same-origin link
          return (
            <Button
              key={key}
              asChild
              variant={mapVariant(btn.type)}
              size={mapSize(btn.size)}
              className={buttonClassName}
            >
              <Link
                href={href}
                target={isBlank ? "_blank" : undefined}
                rel={isBlank ? "noopener noreferrer" : undefined}
              >
                {content}
              </Link>
            </Button>
          );
        }

        // External link (absolute URL)
        return (
          <Button
            key={key}
            asChild
            variant={mapVariant(btn.type)}
            size={mapSize(btn.size)}
            className={buttonClassName}
          >
            <Link
              href={href}
              target={isBlank ? "_blank" : undefined}
              rel={isBlank ? "noopener noreferrer" : undefined}
            >
              {content}
            </Link>
          </Button>
        );
       })}
     </div>
    </>
   );
 };
 
 export default SectionButtonsDialog;


